import { CID } from 'multiformats/cid';
import { base36 } from 'multiformats/bases/base36';
import { base58btc } from 'multiformats/bases/base58';

// Public IPFS HTTP gateways used by the checker and URL converter.
// Path-style (https://host/ipfs/<cid>) — the widest-supported form.
export const IPFS_GATEWAYS = [
  { host: 'ipfs.io', operator: 'IPFS Foundation' },
  { host: 'dweb.link', operator: 'IPFS Foundation' },
  { host: 'w3s.link', operator: 'web3.storage' },
  { host: 'nftstorage.link', operator: 'NFT.Storage' },
  { host: 'trustless-gateway.link', operator: 'IPFS Foundation' },
  { host: 'gateway.pinata.cloud', operator: 'Pinata' },
  { host: '4everland.io', operator: '4EVERLAND' },
  { host: 'ipfs.filebase.io', operator: 'Filebase' },
  { host: 'gateway.lighthouse.storage', operator: 'Lighthouse' },
];

// Multibase prefix char → name (subset covering everything CID.parse accepts)
const MULTIBASE_NAMES = {
  f: 'base16', F: 'base16upper',
  b: 'base32', B: 'base32upper',
  c: 'base32pad', C: 'base32padupper',
  v: 'base32hex', V: 'base32hexupper',
  t: 'base32hexpad', T: 'base32hexpadupper',
  h: 'base32z',
  k: 'base36', K: 'base36upper',
  z: 'base58btc', Z: 'base58flickr',
  m: 'base64', M: 'base64pad',
  u: 'base64url', U: 'base64urlpad',
};

// Common multicodec codes → names (fallback: hex code)
const MULTICODEC_NAMES = {
  0x55: 'raw',
  0x70: 'dag-pb',
  0x71: 'dag-cbor',
  0x72: 'libp2p-key',
  0x73: 'git-raw',
  0x74: 'torrent-file',
  0x75: 'torrent-info',
  0x76: 'leofcoin-block',
  0x85: 'dag-jose',
  0x0129: 'dag-json',
  0x90: 'eth-block',
  0x91: 'eth-block-list',
  0x92: 'eth-tx-trie',
  0x93: 'eth-tx',
  0x94: 'eth-tx-receipt-trie',
  0x95: 'eth-tx-receipt',
  0x96: 'eth-state-trie',
  0x97: 'eth-account-snapshot',
  0x98: 'eth-storage-trie',
  0xb0: 'bitcoin-block',
  0xb1: 'bitcoin-tx',
  0xb2: 'bitcoin-witness-commitment',
  0xc0: 'zcash-block',
  0xc1: 'zcash-tx',
  0xf0: 'dash-block',
  0xf1: 'dash-tx',
  0xfa: 'swarm-manifest',
  0xfb: 'swarm-feed',
};

// Common multihash codes → names (fallback: hex code)
const MULTIHASH_NAMES = {
  0x00: 'identity',
  0x11: 'sha1',
  0x12: 'sha2-256',
  0x13: 'sha2-512',
  0x14: 'sha3-512',
  0x15: 'sha3-384',
  0x16: 'sha3-256',
  0x17: 'sha3-224',
  0x18: 'shake-128',
  0x19: 'shake-256',
  0x1a: 'keccak-224',
  0x1b: 'keccak-256',
  0x1c: 'keccak-384',
  0x1d: 'keccak-512',
  0x1e: 'blake3',
  0x20: 'sha2-384',
  0x56: 'dbl-sha2-256',
  0xd4: 'md4',
  0xd5: 'md5',
  0xb220: 'blake2b-256',
  0xb240: 'blake2b-512',
  0xb260: 'blake2s-256',
};

const WEAK_HASHES = new Set([0x11, 0xd4, 0xd5]);

export function bytesToHex(bytes) {
  return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function codecName(code) {
  return MULTICODEC_NAMES[code] || `unknown (0x${code.toString(16)})`;
}

export function hashName(code) {
  return MULTIHASH_NAMES[code] || `unknown (0x${code.toString(16)})`;
}

function multibaseName(input) {
  if (input.startsWith('Qm')) return 'base58btc';
  return MULTIBASE_NAMES[input[0]] || `unknown (${input[0]})`;
}

/**
 * Decode a CID string (v0 or v1, any multibase) into a full inspection report.
 * Throws an Error with a user-facing message on invalid input.
 */
export function inspectCid(input) {
  const raw = (input || '').trim();
  if (!raw) throw new Error('Enter a CID to inspect');
  let cid;
  try {
    cid = CID.parse(raw);
  } catch {
    throw new Error('Not a valid CID. CIDv0 is 46 chars starting with "Qm"; CIDv1 starts with a multibase prefix like "b" (base32) or "z" (base58).');
  }

  const v0Possible =
    cid.version === 1 &&
    cid.code === 0x70 && // dag-pb
    cid.multihash.code === 0x12 && // sha2-256
    cid.multihash.digest.length === 32;

  const v1 = cid.toV1();
  const base32V1 = v1.toString();
  const base36V1 = v1.toString(base36);
  const v0String = cid.version === 0 ? cid.toString() : v0Possible ? cid.toV0().toString() : null;

  return {
    cid,
    version: cid.version,
    multibase: multibaseName(raw),
    codecCode: cid.code,
    codec: codecName(cid.code),
    hashCode: cid.multihash.code,
    hashFunction: hashName(cid.multihash.code),
    weakHash: WEAK_HASHES.has(cid.multihash.code),
    digestLength: cid.multihash.digest.length,
    digestHex: bytesToHex(cid.multihash.digest),
    multihashHex: bytesToHex(cid.multihash.bytes),
    byteLength: cid.bytes.length,
    base32String: base32V1,
    base36String: base36V1,
    v0: v0String,
    humanReadable: `${multibaseName(raw)} - cidv${cid.version} - ${codecName(cid.code)} : ${hashName(cid.multihash.code)} : ${cid.multihash.digest.length} : ${bytesToHex(cid.multihash.digest)}`,
    // DNS-safe subdomain form needs ≤63 lowercase chars
    subdomainCid: base32V1.length <= 63 ? base32V1 : base36V1.length <= 63 ? base36V1 : null,
    isIpnsName: cid.code === 0x72, // libp2p-key — typically used as an IPNS name
  };
}

/**
 * Parse user input that may be an ipfs:// or ipns:// URI, a gateway URL
 * (path- or subdomain-style), or a bare CID / IPNS name.
 * Returns { kind: 'ipfs' | 'ipns', ref, path, style } — ref is the CID or name string.
 */
export function parseIpfsInput(input) {
  const raw = (input || '').trim();
  if (!raw) throw new Error('Enter a CID, IPNS name, ipfs:// URI, or gateway URL');

  // Native protocol URIs
  const proto = raw.match(/^ipfs:\/\/([^\s/?#]+)([^\s#]*)/i);
  if (proto) return { kind: 'ipfs', ref: proto[1], path: proto[2] || '', style: 'uri' };
  const protoIpns = raw.match(/^ipns:\/\/([^\s/?#]+)([^\s#]*)/i);
  if (protoIpns) return { kind: 'ipns', ref: protoIpns[1], path: protoIpns[2] || '', style: 'uri' };

  // HTTP(S) URLs — path or subdomain gateway style
  const url = raw.match(/^https?:\/\/([^\s/?#]+)([^\s#]*)/i);
  if (url) {
    const host = url[1].toLowerCase();
    const pathname = url[2] || '';
    const pathMatch = pathname.match(/^\/(ipfs|ipns)\/([^\s/?#]+)([^\s#]*)/i);
    if (pathMatch) {
      return { kind: pathMatch[1].toLowerCase(), ref: pathMatch[2], path: pathMatch[3] || '', style: 'path-gateway', host };
    }
    const subMatch = host.match(/^([^.]+)\.(ipfs|ipns)\.(.+)$/i);
    if (subMatch) {
      return { kind: subMatch[2].toLowerCase(), ref: subMatch[1], path: pathname, style: 'subdomain-gateway', host };
    }
    throw new Error('Could not find an /ipfs/ or /ipns/ segment (or an .ipfs./.ipns. subdomain) in that URL');
  }

  // Bare CID (or CID + path). Split first segment as the ref.
  const slashIdx = raw.indexOf('/');
  const ref = slashIdx === -1 ? raw : raw.slice(0, slashIdx);
  const path = slashIdx === -1 ? '' : raw.slice(slashIdx);
  try {
    const cid = CID.parse(ref);
    // libp2p-key CIDs are conventionally IPNS names
    return { kind: cid.code === 0x72 ? 'ipns' : 'ipfs', ref, path, style: 'bare', cid };
  } catch {
    // Not a CID — treat as an IPNS name (DNSLink domain, ENS, or base36 peer id).
    // A long single-label string is almost certainly a mangled CID, not a name.
    const looksLikeCid = !ref.includes('.') && /^[a-zA-Z0-9]+$/.test(ref) && ref.length >= 30;
    if (looksLikeCid) {
      throw new Error('Not a valid CID — it looks like one, but fails to decode. Check for typos or a truncated copy.');
    }
    if (/^[a-z0-9]([a-z0-9.\-_]*[a-z0-9])?$/i.test(ref) && ref.length >= 3) {
      return { kind: 'ipns', ref, path, style: 'bare' };
    }
    throw new Error('Not a valid CID or IPNS name');
  }
}

/**
 * Build the set of URLs for a parsed IPFS/IPNS reference.
 * options.gateway — hostname from IPFS_GATEWAYS.
 */
export function buildIpfsUrls({ kind, ref, path = '', gateway = 'ipfs.io' }) {
  const cleanPath = path ? (path.startsWith('/') ? path : `/${path}`) : '';
  const out = {
    canonical: `${kind}://${ref}${cleanPath}`,
    pathGateway: `https://${gateway}/${kind}/${ref}${cleanPath}`,
    subdomain: null,
    cidInfo: null,
  };
  if (kind === 'ipfs') {
    const info = inspectCid(ref);
    out.cidInfo = info;
    if (info.subdomainCid) {
      out.subdomain = `https://${info.subdomainCid}.ipfs.${gateway}${cleanPath}`;
    }
    // Normalize canonical URI to the v1 base32 form users should share
    out.canonical = `ipfs://${info.base32String}${cleanPath}`;
  } else {
    // IPNS names can use subdomain gateways. TLS wildcards cover a single
    // label only, so DNSLink names are inlined: docs.ipfs.tech → docs-ipfs-tech
    const singleLabel = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/i.test(ref) && ref.length <= 63;
    const dnsName = /^[a-z0-9]([a-z0-9.-]*[a-z0-9])?$/i.test(ref) && ref.length <= 253
      && ref.split('.').every((l) => l.length <= 63 && /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/i.test(l));
    const inlined = singleLabel ? ref : dnsName ? ref.replace(/\./g, '-') : null;
    if (inlined) {
      out.subdomain = `https://${inlined}.ipns.${gateway}${cleanPath}`;
    }
  }
  return out;
}

export function gatewayUrl(host, cid, path = '') {
  const cleanPath = path ? (path.startsWith('/') ? path : `/${path}`) : '';
  return `https://${host}/ipfs/${cid}${cleanPath}`;
}

export const SAMPLE_CIDS = {
  v0: 'QmbWqxBEKC3P8tqsKc98xmWNzrzDtRLMiMPL8wBuTGsMnR',
  v1: 'bafybeigdyrzt5sfp7udm7hu76uh7y26nf3efuylqabf3oclgtqy55fbzdi',
  raw: 'bafkreiczsmdcqvwfnfskbdtppxaqqzs4flbxhhlcylhg53zhzjymxibvym',
};
